import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel, OptioLogoFull } from './SharedComponents';

// ============ SLIDE 26: 10-YEAR OUTLOOK ============
export const SalesOutlookSlide = ({ slide }) => {
  const colors = ['#0068ff', '#8b5cf6', '#f43f5e'];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[#020206]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1000px] bg-[#0068ff] opacity-[0.08] blur-[250px] rounded-full" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#8b5cf6] opacity-[0.04] blur-[150px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#f43f5e] opacity-[0.03] blur-[120px] rounded-full" />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>Market Data</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-10 animate-slide-up">{slide.title}</h2>
        <div className="grid grid-cols-3 gap-6 w-full max-w-4xl mb-8">
          {slide.stats.map((s, idx) => (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="p-8 text-center" style={{ borderColor: `${colors[idx]}25`, background: `linear-gradient(180deg, ${colors[idx]}08, transparent)` }}>
              <div className="text-4xl md:text-5xl font-black mb-3" style={{ color: colors[idx] }}>{s.value}</div>
              <p className="text-white/40 text-sm">{s.label}</p>
            </GlassCard>
          ))}
        </div>
        <GlassCard delay={0.5} className="px-8 py-4 text-center max-w-2xl mb-4" style={{ borderColor: '#0068ff25' }}>
          <span className="text-white font-semibold text-lg">{slide.punchline}</span>
        </GlassCard>
        <p className="text-white/30 text-sm animate-fade-in" style={{ animationDelay: '0.6s' }}>{slide.closing}</p>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 27: EARLY ADOPTION ============
export const SalesEarlySlide = ({ slide }) => {
  const colors = ['#0068ff', '#22c55e', '#f59e0b'];
  const icons = [
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
  ];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060610] to-[#030308]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#0068ff] opacity-[0.06] blur-[180px] rounded-full" />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>Timing</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-12 animate-slide-up">{slide.title}</h2>
        <div className="grid grid-cols-3 gap-6 w-full max-w-4xl">
          {slide.benefits.map((b, idx) => (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="p-8 text-center hover:scale-[1.03] transition-transform duration-300" style={{ borderColor: `${colors[idx]}25`, background: `linear-gradient(180deg, ${colors[idx]}08, transparent)` }}>
              <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${colors[idx]}15`, color: colors[idx] }}>{icons[idx]}</div>
              <h3 className="text-white font-bold text-lg mb-2">{b.name}</h3>
              <p className="text-white/40 text-sm leading-relaxed">{b.description}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 28: PRICING ============
export const SalesPricingSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#040a14] to-[#030308]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[700px] bg-[#0068ff] opacity-[0.07] blur-[200px] rounded-full" />
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <SectionLabel delay={0}>Investment</SectionLabel>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-10 animate-slide-up">{slide.title}</h2>
      <div className="flex items-stretch gap-5 w-full max-w-4xl mb-8">
        {slide.tiers.map((tier, idx) => {
          const isFirst = idx === 0;
          return (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="flex-1 p-7 text-center" style={{
              borderColor: isFirst ? '#0068ff40' : '#0068ff20',
              boxShadow: isFirst ? '0 0 50px rgba(0,104,255,0.12)' : 'none'
            }}>
              {isFirst && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#0068ff] text-white text-[10px] font-bold">STANDARD</div>}
              {tier.discount && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#22c55e] text-white text-[10px] font-bold">{tier.discount}</div>}
              <div className="text-white/40 text-sm mb-3">{tier.range}</div>
              <div className="text-4xl font-black text-[#0068ff] mb-1">{tier.price}</div>
              <div className="text-white/25 text-xs">per node</div>
            </GlassCard>
          );
        })}
      </div>
      <GlassCard delay={0.5} className="px-8 py-5 w-full max-w-md" style={{ borderColor: '#0068ff20' }}>
        <div className="text-[10px] uppercase tracking-widest text-white/25 mb-3 text-center">Quick Examples</div>
        <div className="space-y-2">
          {slide.examples.map((ex, i) => (
            <div key={i} className="flex justify-between items-center py-1.5 border-b border-white/[0.04]">
              <span className="text-white/50 text-sm">{ex.qty}</span>
              <span className="text-[#0068ff] text-sm font-bold">{ex.total}</span>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 29: AUDIENCE ============
export const SalesAudienceSlide = ({ slide }) => {
  const colors = ['#0068ff', '#8b5cf6', '#22c55e'];
  const icons = [
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
  ];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060612] to-[#030308]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-[#0068ff] opacity-[0.06] blur-[180px] rounded-full" />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>Target Audience</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-12 animate-slide-up">{slide.title}</h2>
        <div className="grid grid-cols-3 gap-6 w-full max-w-4xl mb-10">
          {slide.personas.map((p, idx) => (
            <GlassCard key={idx} delay={0.2 + idx * 0.1} className="p-8 text-center hover:scale-[1.03] transition-transform duration-300" style={{ borderColor: `${colors[idx]}25`, background: `linear-gradient(180deg, ${colors[idx]}08, transparent)` }}>
              <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${colors[idx]}15`, color: colors[idx] }}>{icons[idx]}</div>
              <h3 className="text-white font-bold text-lg mb-2">{p.name}</h3>
              <p className="text-white/40 text-sm">{p.description}</p>
            </GlassCard>
          ))}
        </div>
        <GlassCard delay={0.6} className="px-8 py-3">
          <span className="text-white font-semibold text-lg">{slide.closing}</span>
        </GlassCard>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 30: FINAL CTA ============
export const SalesCtaSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-[#020206]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1600px] h-[1200px] bg-[#0068ff] opacity-[0.1] blur-[280px] rounded-full" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#0068ff] opacity-[0.06] blur-[150px] rounded-full" />
      <div className="absolute top-12 left-12 w-20 h-20"><div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#0068ff]/30 to-transparent" /><div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-[#0068ff]/30 to-transparent" /></div>
      <div className="absolute bottom-12 right-12 w-20 h-20"><div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#0068ff]/30 to-transparent" /><div className="absolute bottom-0 right-0 h-full w-[1px] bg-gradient-to-t from-[#0068ff]/30 to-transparent" /></div>
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6 animate-slide-up">{slide.title}</h2>
      <p className="text-white/40 text-lg mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
      <div className="space-y-1 mb-6 text-center animate-fade-in" style={{ animationDelay: '0.15s' }}>
        {slide.points.map((p, i) => <p key={i} className="text-white/35 text-base">{p}</p>)}
      </div>
      <GlassCard delay={0.25} className="px-8 py-4 text-center max-w-2xl mb-8" style={{ borderColor: '#0068ff30' }}>
        <span className="text-white text-xl font-semibold">{slide.message}</span>
      </GlassCard>
      <div className="flex gap-4 mb-8 animate-slide-up" style={{ animationDelay: '0.35s' }}>
        {slide.actions.map((a, idx) => {
          const colors = ['#0068ff', '#8b5cf6', '#22c55e'];
          return (
            <div key={idx} className="px-6 py-3 rounded-xl text-center" style={{ background: `${colors[idx]}12`, border: `1px solid ${colors[idx]}30` }}>
              <span className="text-sm font-bold" style={{ color: colors[idx] }}>{a}</span>
            </div>
          );
        })}
      </div>
      <a href="https://optioblockchain.com/shop" target="_blank" rel="noopener noreferrer" className="animate-scale-in relative z-10 no-underline" style={{ animationDelay: '0.45s' }}>
        <div className="px-12 py-5 rounded-xl bg-[#0068ff] text-white font-bold text-xl tracking-wide cursor-pointer hover:brightness-110 transition-all" style={{ boxShadow: '0 0 60px rgba(0,104,255,0.4)' }}>
          {slide.cta}
        </div>
      </a>
    </div>
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-fade-in" style={{ animationDelay: '0.6s' }}><OptioLogoFull /></div>
  </SlideWrapper>
);
